import { Chip, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { RequirementOutcomeItem } from 'modules/analyse/types/RequirementOutcomeItem';
import { TextBlock } from 'common/ui/TextBlock';
import { sortRequirementOutcomes } from 'modules/analyse/utils/sortRequirementOutcomes';
import { convertWeightToText } from 'modules/analyse/utils/convertWeightToText';
import { AlertCard } from 'common/ui';

const RequirementOutcomeItemComponent: React.FC<{
  requirementOutcome: RequirementOutcomeItem;
}> = ({ requirementOutcome }) => {
  const { isMet, evidence, analysisRequirement } = requirementOutcome;

  if (!analysisRequirement) return null;

  const evidenceText = isMet
    ? evidence ?? 'No evidence found'
    : 'No evidence found';

  return (
    <>
      <Stack direction="row" gap="20px">
        <Chip
          sx={{
            gap: 0
          }}
          color={isMet ? 'success' : 'error'}
          icon={isMet ? <CheckIcon /> : <CloseIcon />}
        />
        <Stack gap="10px">
          <Typography variant="captions">
            {convertWeightToText(analysisRequirement.weight)}
          </Typography>
          <TextBlock
            variant="field"
            title={analysisRequirement.originalRequirementText}
            subtitle={evidenceText}
            titleSx={{
              textDecoration: !isMet ? 'line-through' : undefined
            }}
          />
        </Stack>
      </Stack>
    </>
  );
};

type Props = {
  requirementOutcomes: RequirementOutcomeItem[];
};

export const RequirementOutcomesList: React.FC<Props> = ({
  requirementOutcomes
}) => {
  const splitRequirements = sortRequirementOutcomes(requirementOutcomes);

  return (
    <Stack gap="20px" mt="20px" pt="20px">
      {splitRequirements.mandatory.length > 0 && (
        <>
          <Typography
            sx={{
              fontSize: '18px',
              lineHeight: '28px',
              color: 'customGrey.main'
            }}
          >
            {splitRequirements.mandatoryMet}/
            {splitRequirements.mandatory.length} KO requirements met
          </Typography>
          {splitRequirements.mandatory.length !==
            splitRequirements.mandatoryMet && (
            <AlertCard title="Applicant knocked out" type="info">
              One or more knockout requirements were not met.
            </AlertCard>
          )}
          {splitRequirements.mandatory.map((requirementOutcome) => (
            <RequirementOutcomeItemComponent
              key={requirementOutcome.id}
              requirementOutcome={requirementOutcome}
            />
          ))}
        </>
      )}
      {splitRequirements.optional.length > 0 && (
        <>
          <Typography
            sx={{
              fontSize: '18px',
              lineHeight: '28px',
              color: 'customGrey.main'
            }}
          >
            {splitRequirements.optionalMet}/{splitRequirements.optional.length}{' '}
            Optional requirements met
          </Typography>
          {splitRequirements.optional.map((requirementOutcome) => (
            <RequirementOutcomeItemComponent
              key={requirementOutcome.id}
              requirementOutcome={requirementOutcome}
            />
          ))}
        </>
      )}
    </Stack>
  );
};
